import { useState } from 'react';
import {
  DataTableCellContent,
  DataTableHeaderProps,
  DataTableOnPageChange,
  DataTableOnSearch,
  DataTableOnSort,
  DataTablePaginationProps,
  DataTableProps,
  DataTableSearchProps,
  DataTableSortConfig
} from '../types';
import { paginateData } from './pagination';
import { searchData } from './search';
import { sortData } from './sorting';

type UseData<T extends string> = {
  paginatedData: DataTableCellContent<T>[];
  searchProps: DataTableSearchProps;
  headerProps: DataTableHeaderProps<T>;
  paginationProps: DataTablePaginationProps;
};

/**
 * This hook is used internally by the DataTable component to sort, filter, and
 * paginate data.
 */
export const useData = <T extends string>({
  columns,
  data,
  initialSort,
  onSortChange,
  pagination,
  searchableColumns,
  sortableColumns
}: DataTableProps<T>): UseData<T> => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<DataTableSortConfig<T> | undefined>(
    initialSort
  );
  const [currentPage, setCurrentPage] = useState(1);

  const filteredData = searchData(data, columns, searchableColumns, searchTerm);

  const onSearch: DataTableOnSearch = term => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const sortedData = sortData(filteredData, sortableColumns, sort);

  const onSort: DataTableOnSort<T> = sorting => {
    setSort(sorting);
    setCurrentPage(1);
    onSortChange?.(sorting);
  };

  const totalPages = pagination ? Math.ceil(sortedData.length / pagination) : 1;

  const paginatedData = paginateData(sortedData, pagination, currentPage);

  const onPageChange: DataTableOnPageChange = page => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return {
    paginatedData,
    searchProps: {
      searchTerm,
      onSearch
    },
    headerProps: {
      sort,
      onSort
    },
    paginationProps: {
      currentPage,
      totalPages,
      onPageChange
    }
  };
};
